import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Conversation_Kind } from '@gen/chat/v1/common_pb';
import { useStrings } from '@/i18n/Localization';
import { MEMBER_PAGE_SIZE } from '@/protocol/limits';
import {
  canLinkDiscussion,
  canManageMembers,
  canPublish,
  roleFromEnum,
  viewerFor,
} from '@/protocol/permissions';
import { useLiveQuery } from '@/store/hooks';
import {
  enqueueLinkDiscussion,
  enqueueSubscribeChannel,
  enqueueUnsubscribeChannel,
  getConversation,
  getMember,
  listConversations,
  listMembers,
  setNotificationMode,
} from '@/store/localStore';
import { useSession } from '@/transport/SessionProvider';
import type { Hex } from '@/store/ids';
import type { ConversationRecord, MemberRecord, NotificationMode } from '@/store/types';
import styles from '@/components/panels.module.css';

/**
 * The Section 14 channel info screen: a public/private marker, the subscription
 * control, the notification choice, and admin actions when permitted.
 *
 * The public marker is derived from whether the channel has a public username.
 * The `Conversation` resource carries no visibility flag — Section 06 defines a
 * public channel as one whose posts may be read without membership, and a
 * public username is how that is expressed — so a channel this client has never
 * seen a username for is presented as private, which is the safe direction to
 * be wrong in.
 */
export function ChannelInfoScreen() {
  const { t } = useStrings();
  const navigate = useNavigate();
  const { account, worker } = useSession();
  const params = useParams<{ conversationId: string }>();
  const conversationId = (params.conversationId ?? '') as Hex;

  const [error, setError] = useState<string | null>(null);

  const conversation = useLiveQuery<ConversationRecord | null>(
    () => getConversation(conversationId),
    [conversationId],
    null,
  );
  const ownMember = useLiveQuery<MemberRecord | null>(
    () => (account ? getMember(conversationId, account.id) : Promise.resolve(null)),
    [conversationId, account?.id],
    null,
  );
  const admins = useLiveQuery<MemberRecord[]>(
    () => listMembers(conversationId),
    [conversationId],
    [],
  );
  const groups = useLiveQuery<ConversationRecord[]>(listConversations, [], []);

  const viewer = viewerFor(conversation, ownMember);

  useEffect(() => {
    if (!worker || !canManageMembers(viewer)) {
      return;
    }
    void worker
      .call('list-members', { conversationId, cursor: null, limit: MEMBER_PAGE_SIZE })
      .catch(() => undefined);
  }, [worker, conversationId, viewer]);

  const guard = useCallback(
    async (action: () => Promise<unknown>) => {
      setError(null);
      try {
        await action();
        worker?.notifyOutboxChanged();
      } catch {
        setError(t('status.localFailure'));
      }
    },
    [t, worker],
  );

  if (!account) {
    return null;
  }

  const isPublic = (conversation?.publicUsername ?? null) !== null;
  const linkedId = conversation?.linkedDiscussionId ?? null;
  const linkableGroups = groups.filter(
    (candidate) => candidate.kind === Conversation_Kind.GROUP && !candidate.accessRevoked,
  );

  return (
    <main className={styles.screen} aria-label={t('channel.info')}>
      <header className={styles.screenHeader}>
        <Link to={`/c/${conversationId}`} className={styles.back}>
          {t('nav.back')}
        </Link>
        <h1 className={styles.screenTitle} dir="auto">
          {conversation?.title || t('channel.info')}
        </h1>
      </header>

      <section className={styles.section}>
        {/*
          The marker is a text badge rather than an icon or a colour, so it
          survives a screen reader and 200% text scaling unchanged.
        */}
        <p className={styles.badge}>{isPublic ? t('channel.public') : t('channel.private')}</p>
        {conversation?.publicUsername === null || conversation === null ? null : (
          <p className={styles.rowMeta}>@{conversation.publicUsername}</p>
        )}
        <p className={styles.rowMeta}>
          {t('group.yourRole', {
            role: t(
              viewer.role === 'OWNER'
                ? 'group.roleOwner'
                : viewer.role === 'ADMIN'
                  ? 'group.roleAdmin'
                  : viewer.role === 'MEMBER'
                    ? 'group.roleMember'
                    : 'group.roleNone',
            ),
          })}
        </p>
        {error === null ? null : (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}
      </section>

      {/* --- Subscription -------------------------------------------------- */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('channel.subscribed')}</h2>
        <div className={styles.buttonRow}>
          {conversation?.subscribed ?? false ? (
            <button
              type="button"
              className={styles.button}
              onClick={() => {
                void guard(() =>
                  enqueueUnsubscribeChannel({
                    conversationId,
                    originDeviceId: account.deviceId,
                  }),
                );
              }}
            >
              {t('channel.unsubscribe')}
            </button>
          ) : (
            <button
              type="button"
              className={`${styles.button} ${styles.buttonPrimary}`}
              onClick={() => {
                void guard(() =>
                  enqueueSubscribeChannel({
                    conversationId,
                    originDeviceId: account.deviceId,
                    inviteToken: null,
                  }),
                );
              }}
            >
              {t('channel.subscribe')}
            </button>
          )}
        </div>
      </section>

      {/* --- Notification choice ------------------------------------------- */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('channel.notifications')}</h2>
        <div className={styles.field}>
          <label htmlFor="channel-notifications">{t('channel.notifications')}</label>
          <select
            id="channel-notifications"
            className={styles.select}
            value={conversation?.notificationMode ?? 'MENTIONS'}
            onChange={(event) => {
              const mode = event.target.value as NotificationMode;
              void setNotificationMode(conversationId, mode);
            }}
          >
            {/*
              Section 06 defaults channels to mentions and replies only, so that
              option is listed first and is what a new subscription gets.
            */}
            <option value="MENTIONS">{t('channel.notifyMentions')}</option>
            <option value="ALL">{t('channel.notifyAll')}</option>
            <option value="NONE">{t('channel.notifyNone')}</option>
          </select>
        </div>
      </section>

      {/* --- Comments ------------------------------------------------------ */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('channel.comments')}</h2>
        {linkedId === null ? (
          <p className={styles.empty}>{t('channel.noDiscussion')}</p>
        ) : (
          <button
            type="button"
            className={styles.button}
            onClick={() => {
              // Section 06: comments belong to the linked group, and channel
              // visibility does not bypass its join or history policy. So this
              // navigates there and lets the group's own rules decide what is
              // readable.
              void navigate(`/c/${linkedId}`);
            }}
          >
            {t('channel.openDiscussion')}
          </button>
        )}

        {canLinkDiscussion(viewer) ? (
          <>
            <p className={styles.hint}>{t('channel.linkHint')}</p>
            <div className={styles.field}>
              <label htmlFor="discussion-group">{t('channel.linkDiscussion')}</label>
              <select
                id="discussion-group"
                className={styles.select}
                value={linkedId ?? ''}
                onChange={(event) => {
                  const value = event.target.value;
                  void guard(() =>
                    enqueueLinkDiscussion({
                      channelId: conversationId,
                      groupId: value === '' ? null : (value as Hex),
                      originDeviceId: account.deviceId,
                    }),
                  );
                }}
              >
                <option value="">{t('channel.unlinkDiscussion')}</option>
                {linkableGroups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.title || group.id.slice(0, 8)}
                  </option>
                ))}
              </select>
            </div>
          </>
        ) : null}
      </section>

      {/* --- Administrators ------------------------------------------------ */}
      {canPublish(viewer) ? (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>{t('group.members')}</h2>
          <ul className={styles.list}>
            {admins.map((member) => (
              <li key={member.userId} className={styles.row}>
                <span className={styles.rowBody}>
                  <span className={styles.rowTitle} dir="auto">
                    {member.displayName || member.userId.slice(0, 8)}
                  </span>
                  <span className={styles.rowMeta}>{roleFromEnum(member.role)}</span>
                </span>
              </li>
            ))}
          </ul>
          <p className={styles.hint}>{t('channel.viewsHint')}</p>
        </section>
      ) : null}
    </main>
  );
}
